import type { PricingFetchResult } from "../types/contracts";
import { persistIntegrationDebugFromResponse } from "@src/load-agent/utils/integrationDebugStorage";

function getLoadAgentApiBaseUrl(): string {
  return process.env.NODE_ENV !== "production" ? "/api/" : "";
}

export { getLoadAgentApiBaseUrl };
export class LoadAgentHttpTransport {
  constructor(private readonly basePath: string = getLoadAgentApiBaseUrl()) {}
  get baseUrl(): string {
    return this.basePath;
  }
  private async errorMessageFromResponse(
    res: Response,
    fallbackPrefix = "Fetch failed"
  ): Promise<string> {
    const text = await res.text().catch(() => "");

    if (!text) return `${fallbackPrefix}: ${res.status}`;
    try {
      const parsed = JSON.parse(text) as {
        message?: unknown;
        error?: unknown;
      };

      if (typeof parsed.message === "string" && parsed.message.trim()) return parsed.message;
      if (typeof parsed.error === "string" && parsed.error.trim()) return parsed.error;
    } catch (error) {
      void error;
    }
    return `${fallbackPrefix}: ${res.status} - ${text}`;
  }
  async getJson<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetch(`${this.baseUrl}${path}`, init);

    if (!res.ok) {
      throw new Error(await this.errorMessageFromResponse(res));
    }
    return (await res.json()) as T;
  }
  async postNoContent(path: string, body: unknown, init?: RequestInit): Promise<void> {
    const res = await fetch(`${this.baseUrl}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(init?.headers as Record<string, string> | undefined),
      },
      body: JSON.stringify(body),
      signal: init?.signal,
      credentials: init?.credentials,
    });

    if (!res.ok) {
      throw new Error(await this.errorMessageFromResponse(res));
    }
  }
  async patchNoContent(path: string, body: unknown, init?: RequestInit): Promise<void> {
    const res = await fetch(`${this.baseUrl}${path}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(init?.headers as Record<string, string> | undefined),
      },
      body: JSON.stringify(body),
      signal: init?.signal,
      credentials: init?.credentials,
    });

    if (!res.ok) {
      throw new Error(await this.errorMessageFromResponse(res));
    }
  }
  async postJson<T>(path: string, body: unknown, init?: RequestInit): Promise<T> {
    const res = await fetch(`${this.baseUrl}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(init?.headers as Record<string, string> | undefined),
      },
      body: JSON.stringify(body),
      signal: init?.signal,
      credentials: init?.credentials,
    });

    if (!res.ok) {
      throw new Error(await this.errorMessageFromResponse(res));
    }
    return (await res.json()) as T;
  }
  async postLoadAgentQuote(
    shipmentId: string,
    body: Record<string, unknown>
  ): Promise<PricingFetchResult> {
    const path = `load-agent/loads/${encodeURIComponent(shipmentId)}/quote`;
    const res = await fetch(`${this.baseUrl}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const pricingError = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      const msg = typeof pricingError.message === "string" ? pricingError.message : "";

      throw new Error(msg || "Quote request failed");
    }
    const result = (await res.json().catch(() => ({}))) as {
      offers?: unknown[];
      marketRates?: unknown[];
      error?: string | null;
    };

    persistIntegrationDebugFromResponse(result);
    const offers = Array.isArray(result.offers) ? result.offers : [];
    const marketRates = Array.isArray(result.marketRates) ? result.marketRates : [];

    if (result.error) {
      return { hasOffers: false, message: result.error };
    }
    if (offers.length === 0 && marketRates.length === 0) {
      return { hasOffers: false, message: "No offers returned from the API." };
    }
    return { hasOffers: true, message: "Retrieved offers!" };
  }
  async postLoadAgentReplyDraftHtml(body: {
    internetMessageId: string;
    shipmentIds: string[];
    prospectDisplayName?: string;
  }): Promise<{
    html: string;
  }> {
    return this.postJson<{
      html: string;
    }>("load-agent/reply-draft-html", body);
  }
  async postLoadAgentBook(
    shipmentId: string,
    body: {
      internetMessageId: string;
      selectedOfferId?: string;
    }
  ): Promise<{
    success: boolean;
    confirmationId?: string;
    loadId?: number;
    error?: string;
    bookAttachments?: {
      name: string;
      url: string;
    }[];
    confirmationHtml?: string;
    replyAttachments?: {
      name: string;
      url: string;
    }[];
  }> {
    const path = `load-agent/loads/${encodeURIComponent(shipmentId)}/book`;
    const payload: Record<string, unknown> = {
      internetMessageId: body.internetMessageId,
    };

    if (body.selectedOfferId) {
      payload.selectedOfferId = body.selectedOfferId;
    }
    const res = await fetch(`${this.baseUrl}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;

    // Persist server-captured vendor traces even when booking fails, so integration setup
    // can still show the request/response details for troubleshooting.
    persistIntegrationDebugFromResponse(json);
    if (!res.ok) {
      const msg =
        typeof json.error === "string"
          ? json.error
          : typeof json.message === "string"
            ? json.message
            : `Book request failed (HTTP ${res.status})`;

      throw new Error(msg);
    }
    if (json.success !== true) {
      const err = typeof json.error === "string" ? json.error : "Booking failed";

      throw new Error(err);
    }
    const rawAttachments = json.bookAttachments;
    const bookAttachments = Array.isArray(rawAttachments)
      ? rawAttachments
          .map((row) => {
            if (!row || typeof row !== "object" || Array.isArray(row)) return null;
            const o = row as Record<string, unknown>;
            const name = typeof o.name === "string" ? o.name.trim() : "";
            const url = typeof o.url === "string" ? o.url.trim() : "";

            if (!name || !url) return null;
            return { name, url };
          })
          .filter(
            (
              x
            ): x is {
              name: string;
              url: string;
            } => x !== null
          )
      : undefined;
    const rawReply = json.replyAttachments;
    const replyAttachments = Array.isArray(rawReply)
      ? rawReply
          .map((row) => {
            if (!row || typeof row !== "object" || Array.isArray(row)) return null;
            const o = row as Record<string, unknown>;
            const name = typeof o.name === "string" ? o.name.trim() : "";
            const url = typeof o.url === "string" ? o.url.trim() : "";

            if (!name || !url) return null;
            return { name, url };
          })
          .filter(
            (
              x
            ): x is {
              name: string;
              url: string;
            } => x !== null
          )
      : [];

    return {
      success: true,
      confirmationId: typeof json.confirmationId === "string" ? json.confirmationId : undefined,
      loadId: typeof json.loadId === "number" ? json.loadId : undefined,
      bookAttachments: bookAttachments && bookAttachments.length > 0 ? bookAttachments : undefined,
      confirmationHtml:
        typeof json.confirmationHtml === "string" ? json.confirmationHtml : undefined,
      replyAttachments: replyAttachments.length > 0 ? replyAttachments : undefined,
    };
  }
}
